// Lazy drill-down for one (budget line, FY, period|quarter) dashboard cell.
// Fetches GET /api/budget/drilldown/ on open and shows the two §4 sections:
// SAP rows feeding Actual — with an `is_accrual` badge (no accrual-group
// link this phase: the linker is deferred, so `accrual_group_id` is always
// NULL) — and committed invoices linking to the invoice detail page.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TOKENS, MONO_FONT, NUM_STYLE, CODE_STYLE, fmtMoney } from '../reconciliation/tokens';
import { Modal, Tone, ConfidenceChip, SectionLabel, styles } from '../reconciliation/primitives';
import { fetchDrilldown, fetchBucketDrilldown, fetchActualToBudgetDrilldown } from './api';
import AliasPopover from './AliasPopover';

// Locked-USD display with the needs-FX fallback rule (§4): amount_usd when
// present, the raw USD amount when the row is already USD, otherwise the
// original currency flagged as awaiting FX backfill.
function usdCell(row) {
  if (row.amount_usd != null) return fmtMoney(Number(row.amount_usd), 'USD');
  if (row.currency === 'USD' && row.amount != null) {
    return fmtMoney(Number(row.amount), 'USD');
  }
  return row.amount != null ? fmtMoney(Number(row.amount), row.currency) : '';
}

function needsFx(row) {
  return row.amount_usd == null && row.currency && row.currency !== 'USD';
}

function fmtDate(s) {
  if (!s) return '';
  // Posting/invoice dates arrive as date-only ISO strings; parsing them
  // with `new Date` shifts them a day back in negative-offset timezones
  // (UTC-midnight parse) — format the calendar date directly instead.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US');
}

export default function CellDrilldownDrawer({ cell, onClose, onToast, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The SAP row being attributed (A2B drill only) + a local refresh counter so
  // the drawer re-fetches its own {sap_rows, invoices} after a project save.
  const [aliasRow, setAliasRow] = useState(null);
  const [refresh, setRefresh] = useState(0);

  // Escape-to-close is handled by the shared Modal primitive — no local
  // keydown listener needed here.

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // Three drill-down flavors, all returning the same {sap_rows, invoices}:
    //  - cell.a2b: project-scoped (A2B tab) — keyed by the row's effective
    //    project_id, or a single SAP tx (synthetic SAP-without-project row);
    //  - cell.bucket: a Dashboard exception bucket (FY-wide);
    //  - otherwise: a Dashboard (budget line, period|quarter) cell.
    let req;
    if (cell.a2b) {
      req = fetchActualToBudgetDrilldown(cell.fiscalYear, {
        projectId: cell.projectId,
        sapTransactionId: cell.sapTransactionId,
      });
    } else if (cell.bucket) {
      req = fetchBucketDrilldown(cell.bucket, cell.fiscalYear);
    } else {
      req = fetchDrilldown(cell.lineId, cell.fiscalYear, {
        period: cell.period,
        quarter: cell.quarter,
      });
    }
    req
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load drill-down');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cell, refresh]);

  const sapRows = data?.sap_rows || [];
  const invoices = data?.invoices || [];

  // Attribution is offered only on the A2B (project-scoped) drill, where
  // attributing a SAP row to a project is the manual-override mechanism. The
  // handler creates NO budget line (lazy create only on a budget edit, design
  // §4); after save it refreshes the drawer and bumps the parent grid. The
  // shared AliasPopover keys on `sap_transaction_id` (so it runs in Update-
  // SAP-Record mode) and seeds the picker from the row's effective project.
  const canAlias = Boolean(cell.a2b) && typeof onToast === 'function';
  const aliasRowOf = (r) => ({
    sap_transaction_id: r.id,
    name: r.project_name || r.text_field || r.reference_field || r.sap_document_number || '',
    project_id: r.project_id || null,
  });
  const onAliasChanged = () => {
    setRefresh((n) => n + 1);
    if (typeof onChanged === 'function') onChanged();
  };

  return (
    <>
    <Modal
      title={cell.bucket ? cell.bucketTitle : cell.lineName}
      subtitle={
        cell.bucket || cell.a2b
          ? `FY${cell.fiscalYear}`
          : `FY${cell.fiscalYear} · ${cell.label}`
      }
      onClose={onClose}
    >
      {/* Marker class lets Budget.css widen the shared Modal dialog for this
          drill-down (min(1100px, 92vw)) without editing the primitive. */}
      <div className="budget-drill-modal">
          {loading ? (
            <div className="budget-empty">
              <div className="title">Loading drill-down…</div>
              <div className="sub">Fetching SAP rows and committed invoices for this cell.</div>
            </div>
          ) : error ? (
            <div className="budget-empty">
              <div className="title">Couldn't load drill-down</div>
              <div className="sub">{error}</div>
            </div>
          ) : (
            <>
              {sapRows.length > 0 && invoices.length > 0 && (
                <div className="bd-recon-hint">
                  See a possible match?{' '}
                  <Link to="/reconciliation" className="bd-recon-link">Open Reconciliation</Link>
                </div>
              )}
              <div className="bd-section">
                <SectionLabel>SAP rows · Actual ({sapRows.length})</SectionLabel>
                {sapRows.length === 0 ? (
                  <div style={{ fontSize: 11.5, color: TOKENS.ink3, padding: '8px 0' }}>
                    No SAP postings in this cell.
                  </div>
                ) : (
                  <table style={styles.tableBase}>
                    <thead>
                      <tr>
                        <th style={styles.thStyle}>Doc</th>
                        <th style={styles.thStyle}>Posted</th>
                        <th style={styles.thStyle}>Reference / text</th>
                        <th style={styles.thStyle}>Project</th>
                        <th style={styles.thStyle}>Source</th>
                        <th style={{ ...styles.thStyle, textAlign: 'right' }}>Amount</th>
                        {canAlias && <th style={styles.thStyle} aria-label="Update" />}
                      </tr>
                    </thead>
                    <tbody>
                      {sapRows.map((r) => (
                        <tr
                          key={r.id}
                          className="budget-row"
                          style={r.is_accrual ? { opacity: 0.55 } : undefined}
                        >
                          <td style={styles.tdStyle}>
                            <Link
                              to="/budget/review"
                              style={{ textDecoration: 'none', color: 'inherit' }}
                              title="Open review queue"
                            >
                              <div className="rq-doc" style={{ color: TOKENS.accent }}>{r.sap_document_number}</div>
                            </Link>
                            <div className="rq-meta">
                              {r.document_type}
                              {r.posting_key ? ` · PK ${r.posting_key}` : ''}
                              {r.is_accrual && (
                                <>
                                  <Tone tone="soft">accrual</Tone>
                                  <span
                                    style={{
                                      fontFamily: MONO_FONT,
                                      fontSize: 10,
                                      color: TOKENS.ink3,
                                      marginLeft: 3,
                                    }}
                                    title="Accruals don't count against the budget yet — pending client accrual rules"
                                  >
                                    · ignored
                                  </span>
                                </>
                              )}
                            </div>
                          </td>
                          <td style={styles.tdStyle}>
                            <span style={{ ...NUM_STYLE, fontSize: 10.5 }}>
                              {fmtDate(r.posting_date)}
                            </span>
                          </td>
                          <td style={{ ...styles.tdStyle, maxWidth: 180 }}>
                            <div className="rq-details" title={r.text_field || ''}>
                              {r.text_field || '—'}
                            </div>
                            {r.reference_field && (
                              <div className="rq-details ref">{r.reference_field}</div>
                            )}
                          </td>
                          <td style={styles.tdStyle}>
                            <span className="bd-project-name">{r.project_name || '—'}</span>
                          </td>
                          <td style={styles.tdStyle}>
                            <div className="rq-suggested">
                              {r.classification_source ? (
                                <>
                                  <span className="src">{r.classification_source}</span>
                                  {r.confidence != null && (
                                    <ConfidenceChip value={r.confidence} />
                                  )}
                                </>
                              ) : (
                                <span className="src">—</span>
                              )}
                            </div>
                          </td>
                          <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                            <span style={{ ...NUM_STYLE, fontSize: 11 }}>
                              {usdCell(r)}
                            </span>
                            {needsFx(r) && (
                              <div>
                                <Tone tone="soft" title="Excluded from sums until FX backfill">
                                  needs FX
                                </Tone>
                              </div>
                            )}
                          </td>
                          {canAlias && (
                            <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                              <button
                                className="bd-alias-btn"
                                title="Update SAP Record (attribute this posting to a project)"
                                onClick={() => setAliasRow(aliasRowOf(r))}
                              >
                                Update
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="bd-section">
                <SectionLabel>Committed invoices ({invoices.length})</SectionLabel>
                {invoices.length === 0 ? (
                  <div style={{ fontSize: 11.5, color: TOKENS.ink3, padding: '8px 0' }}>
                    No committed invoices in this cell.
                  </div>
                ) : (
                  <table style={styles.tableBase}>
                    <thead>
                      <tr>
                        <th style={styles.thStyle}>Invoice</th>
                        <th style={styles.thStyle}>Vendor</th>
                        <th style={styles.thStyle}>Project</th>
                        <th style={styles.thStyle}>Date</th>
                        <th style={styles.thStyle}>Status</th>
                        <th style={{ ...styles.thStyle, textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv) => (
                        <tr key={inv.id} className="budget-row">
                          <td style={styles.tdStyle}>
                            <Link
                              to={`/invoice/${inv.id}`}
                              style={{
                                ...CODE_STYLE,
                                fontSize: 11,
                                color: TOKENS.accent,
                                textDecoration: 'none',
                              }}
                            >
                              {inv.invoice_number || inv.id.slice(0, 8)}
                            </Link>
                          </td>
                          <td style={styles.tdStyle}>{inv.vendor || '—'}</td>
                          <td style={styles.tdStyle}>
                            <span className="bd-project-name">{inv.project_name || '—'}</span>
                          </td>
                          <td style={styles.tdStyle}>
                            <span style={{ ...NUM_STYLE, fontSize: 10.5 }}>
                              {fmtDate(inv.invoice_date)}
                            </span>
                          </td>
                          <td style={styles.tdStyle}>
                            <span className="rq-meta">{inv.status}</span>
                          </td>
                          {/* Regular-size figure (no shrink) — committed invoices
                              read at the same weight as reconciled actuals (item 2). */}
                          <td style={{ ...styles.tdStyle, textAlign: 'right' }}>
                            <span style={NUM_STYLE}>
                              {usdCell(inv)}
                            </span>
                            {needsFx(inv) && (
                              <div>
                                <Tone tone="soft" title="Excluded from sums until FX backfill">
                                  needs FX
                                </Tone>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
      </div>
    </Modal>
    {aliasRow && (
      <AliasPopover
        row={aliasRow}
        onClose={() => setAliasRow(null)}
        onToast={onToast}
        onChanged={onAliasChanged}
      />
    )}
    </>
  );
}
